import { AZURE_FEED_PREFIX, AZURE_STORAGE_PREFIX, BASE_URL } from "constant/env-variables";

const parseMaybeJson = (value) => {
  if (typeof value !== "string") {
    return value;
  }

  const trimmed = value.trim();
  if (!trimmed.startsWith("[") && !trimmed.startsWith("{")) {
    return value;
  }

  try {
    return JSON.parse(trimmed);
  } catch (_error) {
    return value;
  }
};

const isAbsoluteUrl = (value) =>
  typeof value === "string" &&
  (/^(https?:)?\/\//.test(value) || value.startsWith("blob:") || value.startsWith("data:"));

const getRawPhotoUrl = (entry) => {
  if (!entry) {
    return null;
  }

  if (typeof entry === "string") {
    return entry;
  }

  if (typeof entry?.photo === "string") {
    return entry.photo;
  }

  if (entry?.photo && typeof entry.photo === "object") {
    return entry.photo.url || entry.photo.slug || entry.photo.key || null;
  }

  if (typeof entry?.Photo === "string") {
    return entry.Photo;
  }

  if (entry?.Photo && typeof entry.Photo === "object") {
    return entry.Photo.url || entry.Photo.slug || entry.Photo.key || null;
  }

  return entry.url || entry.image || entry.slug || entry.key || null;
};

const normalizePhotoEntry = (entry) => {
  const url = getRawPhotoUrl(entry);

  if (!url) {
    return null;
  }

  const defaultValue =
    typeof entry === "object" ? Boolean(entry.default_value ?? entry.default ?? entry.isDefault) : false;

  return {
    photo: {
      url
    },
    default_value: defaultValue
  };
};

const getRawPhotoPayload = (productService) => {
  const photosField = parseMaybeJson(productService?.photos ?? productService?.Photos ?? null);
  if (Array.isArray(photosField) && photosField.length) {
    return photosField;
  }

  const photoField = parseMaybeJson(
    productService?.photo ?? productService?.Photo ?? productService?.url ?? productService?.image ?? null
  );

  if (Array.isArray(photoField) && photoField.length) {
    return photoField;
  }

  if (photoField != null && photoField !== "" && photoField !== "-") {
    return photoField;
  }

  return null;
};

const getLegacyPhotoFieldRaw = (productService) =>
  productService?.photo ?? productService?.Photo ?? productService?.url ?? productService?.image ?? null;

const getLegacyPhotoMetadataArray = (productService) => {
  const legacyRaw = getLegacyPhotoFieldRaw(productService);
  if (legacyRaw == null || legacyRaw === "" || legacyRaw === "-") {
    return null;
  }

  const parsedLegacy = parseMaybeJson(legacyRaw);
  return Array.isArray(parsedLegacy) ? parsedLegacy : null;
};

const mergeDefaultFlagsFromLegacyPhotoJson = (photos, productService) => {
  const legacyEntries = getLegacyPhotoMetadataArray(productService);
  if (!legacyEntries?.length) {
    return photos;
  }

  return photos.map((item) => {
    const itemSlug = extractPhotoStorageKey(item.photo?.url);
    if (!itemSlug) {
      return item;
    }

    const legacyEntry = legacyEntries.find((entry) => {
      const legacySlug = extractPhotoStorageKey(getRawPhotoUrl(entry));
      return legacySlug && legacySlug === itemSlug;
    });

    if (!legacyEntry) {
      return item;
    }

    return {
      ...item,
      default_value: Boolean(legacyEntry.default_value ?? legacyEntry.default ?? legacyEntry.isDefault)
    };
  });
};

const resolveDefaultPhotoIndexFromLegacy = (photos, productService) => {
  const legacyRaw = getLegacyPhotoFieldRaw(productService);
  if (legacyRaw == null || legacyRaw === "" || legacyRaw === "-") {
    return -1;
  }

  const parsedLegacy = parseMaybeJson(legacyRaw);
  if (Array.isArray(parsedLegacy)) {
    return -1;
  }

  const legacySlug = extractPhotoStorageKey(getRawPhotoUrl(parsedLegacy) || String(parsedLegacy));
  if (!legacySlug) {
    return -1;
  }

  return photos.findIndex((item) => {
    const itemSlug = extractPhotoStorageKey(item.photo?.url);
    return itemSlug && itemSlug === legacySlug;
  });
};

export const normalizePhotos = (productService) => {
  const rawPayload = getRawPhotoPayload(productService);

  if (Array.isArray(rawPayload)) {
    let photos = rawPayload.map(normalizePhotoEntry).filter(Boolean);
    if (!photos.length) {
      return [];
    }

    photos = mergeDefaultFlagsFromLegacyPhotoJson(photos, productService);

    const defaultIndices = photos.map((item, index) => (item.default_value ? index : -1)).filter((index) => index >= 0);

    if (defaultIndices.length > 1) {
      const keepIndex = defaultIndices[0];
      return photos.map((item, index) => ({
        ...item,
        default_value: index === keepIndex
      }));
    }

    if (!defaultIndices.length) {
      const legacyMatchIndex = resolveDefaultPhotoIndexFromLegacy(photos, productService);
      if (legacyMatchIndex >= 0) {
        return photos.map((item, index) => ({
          ...item,
          default_value: index === legacyMatchIndex
        }));
      }
      photos[0].default_value = true;
    }

    return photos;
  }

  const normalizedSingle = normalizePhotoEntry(rawPayload);
  if (!normalizedSingle) {
    return [];
  }

  return [
    {
      ...normalizedSingle,
      default_value: true
    }
  ];
};

const normalizeFeedStoragePath = (photoKey) => {
  const trimmed = photoKey.trim();

  if (trimmed.includes("/photo/")) {
    if (trimmed.startsWith(AZURE_FEED_PREFIX)) {
      return trimmed;
    }

    const suffix = trimmed.split("/photo/").pop();
    return `${AZURE_FEED_PREFIX}/photo/${suffix}`;
  }

  return `${AZURE_FEED_PREFIX}/photo/${trimmed}`;
};

export const isIntegrationSourcedProduct = (productService) => {
  const integrationId = productService?.integration_id ?? productService?.integrationId;
  return integrationId != null && integrationId !== -1;
};

export const isExternalFeedProduct = (productService) => {
  if (!productService) {
    return false;
  }

  if (productService.distributor) {
    return true;
  }

  return (
    isIntegrationSourcedProduct(productService) && !productService.document_item_product_service_id
  );
};

export const isFeedPhotoStoragePath = (url) =>
  typeof url === "string" && (url.includes("/photo/") || isExternalFeedProxyUrl(url));

/** Resolve the feed storage key from a proxy url, relative feed path, or slug. */
export const resolveFeedPhotoKey = (url) => {
  if (!url || typeof url !== "string") {
    return "";
  }

  const trimmed = url.trim();
  if (!trimmed) {
    return "";
  }

  const fromProxy = extractFeedPhotoKeyFromProxyUrl(trimmed);
  if (fromProxy) {
    return fromProxy;
  }

  if (trimmed.includes("/photo/") && !isDocumentProductServiceStorageUrl(trimmed)) {
    return trimmed.split("/photo/").pop() || "";
  }

  if (!isAbsoluteUrl(trimmed)) {
    return trimmed;
  }

  return "";
};

export const shouldUploadFeedPhotoToDocument = (photoEntry, productService) => {
  if (photoEntry?._pendingFile || photoEntry?._base64) {
    return false;
  }

  const url = photoEntry?.photo?.url || "";
  if (!url || url.startsWith("blob:") || url.startsWith("data:")) {
    return false;
  }

  // Existing document item with an already-uploaded slug (edit save) — do not re-fetch from feed
  if (
    productService?.document_item_product_service_id &&
    !photoEntry?._feedKey &&
    !isExternalFeedProxyUrl(url) &&
    !(isAbsoluteUrl(url) && !shouldNormalizeToCanonicalAzureUrl(url))
  ) {
    return false;
  }

  if (isExternalFeedProxyUrl(url) || photoEntry?._feedKey) {
    return true;
  }

  if (!isIntegrationSourcedProduct(productService) && !isExternalFeedProduct(productService)) {
    return false;
  }

  if (isDocumentProductServiceStorageUrl(url)) {
    return false;
  }

  if ((url.match(/productServicePhoto/g) || []).length > 0) {
    return false;
  }

  return true;
};

/** URL to fetch when copying an external-feed photo into document storage. */
export const getFeedPhotoFetchUrl = (photoEntry) => {
  const url = photoEntry?.photo?.url || "";
  if (!url) {
    return "";
  }

  if (isExternalFeedProxyUrl(url)) {
    return url;
  }

  if (isAbsoluteUrl(url) && !shouldNormalizeToCanonicalAzureUrl(url)) {
    return url;
  }

  const key = photoEntry?._feedKey || resolveFeedPhotoKey(url) || extractPhotoStorageKey(url);
  return key ? getExternalFeedPhotoUrl(key) : "";
};

export const enrichPhotoEntryForDocumentDisplay = (entry, product) => {
  const rawUrl = typeof entry?.photo === "string" ? entry.photo : entry?.photo?.url;
  if (!rawUrl) {
    return entry;
  }

  const feedKeyFromProxy =
    (isExternalFeedProxyUrl(rawUrl) ? extractFeedPhotoKeyFromProxyUrl(rawUrl) : null) || entry?._feedKey || null;

  if (rawUrl.startsWith("blob:") || rawUrl.startsWith("data:")) {
    return {
      ...entry,
      ...(feedKeyFromProxy ? { _feedKey: feedKeyFromProxy } : {}),
      photo: { url: rawUrl }
    };
  }

  if (isAbsoluteUrl(rawUrl) && !shouldNormalizeToCanonicalAzureUrl(rawUrl)) {
    return { ...entry, photo: { url: rawUrl } };
  }

  const slug = extractPhotoStorageKey(rawUrl);
  const isFeedProduct = isExternalFeedProduct(product) || isIntegrationSourcedProduct(product);
  const alreadyOnDocument =
    Boolean(product?.document_item_product_service_id) &&
    !feedKeyFromProxy &&
    !isFeedPhotoStoragePath(rawUrl) &&
    !(isAbsoluteUrl(rawUrl) && !shouldNormalizeToCanonicalAzureUrl(rawUrl));
  const feedKey =
    feedKeyFromProxy ||
    (isFeedPhotoStoragePath(rawUrl) ? resolveFeedPhotoKey(rawUrl) : null) ||
    (isFeedProduct && !alreadyOnDocument ? slug : null);

  return {
    ...entry,
    ...(feedKey ? { _feedKey: feedKey } : {}),
    photo: { url: slug || rawUrl || "" }
  };
};

export const getExternalFeedPhotoUrl = (photoKey) => {
  if (!photoKey || typeof photoKey !== "string") {
    return "";
  }

  if (isAbsoluteUrl(photoKey)) {
    return photoKey;
  }

  return `${BASE_URL}/api/tenantUser/product_feed_image?url=${normalizeFeedStoragePath(photoKey)}`;
};

const isDocumentProductServiceStorageUrl = (rawUrl) =>
  typeof rawUrl === "string" && /\/documents\/[^/]+\/product_service\//.test(rawUrl);

const shouldNormalizeToCanonicalAzureUrl = (rawUrl) => {
  if (!rawUrl || rawUrl.startsWith("blob:") || rawUrl.startsWith("data:")) {
    return false;
  }

  if (isExternalFeedProxyUrl(rawUrl)) {
    return false;
  }

  if (isDocumentProductServiceStorageUrl(rawUrl)) {
    return true;
  }

  if ((rawUrl.match(/productServicePhoto/g) || []).length > 1) {
    return true;
  }

  // Upload API may return a relative storage path without a scheme
  if (!isAbsoluteUrl(rawUrl) && rawUrl.includes("/")) {
    return true;
  }

  return false;
};

const TEMP_PRODUCT_SERVICE_PHOTO_PATH_RE = /temp\/productServicePhoto\/[^/]+\/[^/]+$/;
const PRODUCT_SERVICE_PHOTO_PATH_RE = /productServicePhoto\/[^/]+\/[^/]+$/;

export const isTempProductServicePhotoPath = (url) =>
  typeof url === "string" &&
  (TEMP_PRODUCT_SERVICE_PHOTO_PATH_RE.test(url) || url.startsWith("temp/productServicePhoto/"));

export const isPermanentProductServicePhotoPath = (url) =>
  typeof url === "string" &&
  !isTempProductServicePhotoPath(url) &&
  (PRODUCT_SERVICE_PHOTO_PATH_RE.test(url) || /^productServicePhoto\//.test(url));

export const isOnSettingsAzureStorage = (url) =>
  isTempProductServicePhotoPath(url) || isPermanentProductServicePhotoPath(url);

const photoEntryHasPendingUpload = (entry) =>
  Boolean(
    entry?._pendingFile ||
      entry?._base64 ||
      (entry?.photo?.url &&
        (entry.photo.url.startsWith("blob:") || entry.photo.url.startsWith("data:")))
  );

const shouldResolvePhotoAsExternalFeed = (rawUrl, entry, productService, options = {}) => {
  if (!rawUrl || photoEntryHasPendingUpload(entry)) {
    return false;
  }
  if (isOnSettingsAzureStorage(rawUrl)) {
    return false;
  }
  return Boolean(options.preferExternalFeed || isExternalFeedProduct(productService));
};

const resolveExternalFeedPhotoUrl = (entry, rawUrl) => {
  const slug = extractPhotoStorageKey(rawUrl);
  const feedKey = entry?._feedKey || resolveFeedPhotoKey(rawUrl) || slug;
  return feedKey ? getExternalFeedPhotoUrl(feedKey) : "";
};

/** Relative storage path after tenant id, e.g. temp/productServicePhoto/{id}/file.jpg */
export const extractProductServicePhotoStoragePath = (url) => {
  if (!url || typeof url !== "string") {
    return "";
  }

  const trimmed = url.trim();
  if (!trimmed || trimmed.startsWith("blob:") || trimmed.startsWith("data:")) {
    return trimmed;
  }

  const withoutQuery = trimmed.split("?")[0];
  const tempMatch = withoutQuery.match(TEMP_PRODUCT_SERVICE_PHOTO_PATH_RE);
  if (tempMatch) {
    return tempMatch[0];
  }

  const psMatch = withoutQuery.match(PRODUCT_SERVICE_PHOTO_PATH_RE);
  if (psMatch) {
    return psMatch[0];
  }

  return extractPhotoStorageKey(url);
};

const resolveSettingsPhotoStorageUrl = (rawUrl, tenantId, productService) => {
  const storagePath = extractProductServicePhotoStoragePath(rawUrl);
  if (!tenantId || !storagePath) {
    return "";
  }

  if (storagePath.startsWith("temp/productServicePhoto/") || storagePath.startsWith("productServicePhoto/")) {
    return `${AZURE_STORAGE_PREFIX}/${tenantId}/${storagePath}`;
  }

  if (productService?.product_service_id) {
    return `${AZURE_STORAGE_PREFIX}/${tenantId}/productServicePhoto/${productService.product_service_id}/${storagePath}`;
  }

  return "";
};

export const resolvePhotoEntryUrl = (entry, productService, tenantId, options = {}) => {
  const rawUrl = getRawPhotoUrl(entry);
  if (!rawUrl || typeof rawUrl !== "string") {
    return "";
  }

  if (rawUrl.startsWith("blob:") || rawUrl.startsWith("data:")) {
    return rawUrl;
  }

  const slug = extractPhotoStorageKey(rawUrl);

  if (isTempProductServicePhotoPath(rawUrl)) {
    const tempUrl = resolveSettingsPhotoStorageUrl(rawUrl, tenantId, productService);
    if (tempUrl) {
      return tempUrl;
    }
  }

  if (shouldResolvePhotoAsExternalFeed(rawUrl, entry, productService, options)) {
    const feedUrl = resolveExternalFeedPhotoUrl(entry, rawUrl);
    if (feedUrl) {
      return feedUrl;
    }
  }

  if (isPermanentProductServicePhotoPath(rawUrl)) {
    const permanentUrl = resolveSettingsPhotoStorageUrl(rawUrl, tenantId, productService);
    if (permanentUrl) {
      return permanentUrl;
    }
  }

  if (isAbsoluteUrl(rawUrl) && !shouldNormalizeToCanonicalAzureUrl(rawUrl)) {
    return rawUrl;
  }

  if (!tenantId || !slug) {
    return "";
  }

  if (productService?.document_item_product_service_id) {
    return `${AZURE_STORAGE_PREFIX}/${tenantId}/documents/${productService.document_item_product_service_id}/product_service/${slug}`;
  }

  if (productService?.product_service_id && !shouldResolvePhotoAsExternalFeed(rawUrl, entry, productService, options)) {
    return `${AZURE_STORAGE_PREFIX}/${tenantId}/productServicePhoto/${productService.product_service_id}/${slug}`;
  }

  return "";
};

export const resolvePhotoDisplayUrl = (entry, productService, tenantId, options = {}) => {
  const rawSlug = getRawPhotoUrl(entry);
  if (!rawSlug || typeof rawSlug !== "string") {
    return "";
  }

  if (rawSlug.startsWith("blob:") || rawSlug.startsWith("data:")) {
    return rawSlug;
  }

  const slug = extractPhotoStorageKey(rawSlug);

  if (isTempProductServicePhotoPath(rawSlug)) {
    const tempUrl = resolveSettingsPhotoStorageUrl(rawSlug, tenantId, productService);
    if (tempUrl) {
      return tempUrl;
    }
  }

  if (shouldResolvePhotoAsExternalFeed(rawSlug, entry, productService, options)) {
    const feedUrl = resolveExternalFeedPhotoUrl(entry, rawSlug);
    if (feedUrl) {
      return feedUrl;
    }
  }

  if (isPermanentProductServicePhotoPath(rawSlug)) {
    const permanentUrl = resolveSettingsPhotoStorageUrl(rawSlug, tenantId, productService);
    if (permanentUrl) {
      return permanentUrl;
    }
  }

  if (isAbsoluteUrl(rawSlug) && !shouldNormalizeToCanonicalAzureUrl(rawSlug)) {
    return rawSlug;
  }

  if (tenantId && productService?.document_item_product_service_id && slug) {
    return `${AZURE_STORAGE_PREFIX}/${tenantId}/documents/${productService.document_item_product_service_id}/product_service/${slug}`;
  }

  if (
    tenantId &&
    productService?.product_service_id &&
    slug &&
    !shouldResolvePhotoAsExternalFeed(rawSlug, entry, productService, options)
  ) {
    return `${AZURE_STORAGE_PREFIX}/${tenantId}/productServicePhoto/${productService.product_service_id}/${slug}`;
  }

  return slug ? getExternalFeedPhotoUrl(slug) || slug : "";
};

const resolvePendingPhotoDisplayUrl = (entry) => {
  const url = entry?.photo?.url || "";
  if (url.startsWith("blob:") || url.startsWith("data:") || /^https?:\/\//.test(url)) {
    return url;
  }

  if (entry?._base64) {
    return entry._base64;
  }

  if (entry?._pendingFile instanceof File) {
    return URL.createObjectURL(entry._pendingFile);
  }

  return url;
};

export const resolvePhotoEntryDisplayUrl = (entry, productService, tenantId, options = {}) => {
  const pendingUrl = resolvePendingPhotoDisplayUrl(entry);
  if (photoEntryNeedsSettingsUpload(entry)) {
    return pendingUrl;
  }

  const normalizedEntry = {
    photo: { url: entry?.photo?.url || pendingUrl || "" },
    default_value: Boolean(entry?.default_value)
  };
  const displayOptions = {
    preferExternalFeed: options.preferExternalFeed ?? isExternalFeedProduct(productService),
    ...options
  };

  return (
    resolvePhotoDisplayUrl(normalizedEntry, productService, tenantId, displayOptions) ||
    resolvePhotoEntryUrl(normalizedEntry, productService, tenantId, displayOptions) ||
    pendingUrl ||
    ""
  );
};

export const getDefaultPhotoUrl = (productService, tenantId) => {
  const normalized = normalizePhotos(productService);
  const rawEntries = getPhotoEntriesWithPendingMetadata(productService);
  const entries = rawEntries.length ? rawEntries : normalized;

  if (!entries.length) {
    return "";
  }

  const defaultIndex = normalized.length
    ? normalized.findIndex((item) => item.default_value)
    : getDefaultPhotoIndex(entries);

  const resolvedDefaultIndex = defaultIndex >= 0 ? defaultIndex : 0;
  const defaultRawEntry = entries[resolvedDefaultIndex] || entries[0];
  const defaultNormalizedEntry = normalized[resolvedDefaultIndex] || normalized[0];

  return (
    resolvePhotoEntryDisplayUrl(defaultRawEntry, productService, tenantId) ||
    (defaultNormalizedEntry
      ? resolvePhotoDisplayUrl(defaultNormalizedEntry, productService, tenantId)
      : "") ||
    ""
  );
};

/**
 * External-feed proxy URL for the default photo, used as a display fallback while
 * an external-feed product's image is still being copied into document storage.
 *
 * After an external-feed item is added to a document, its photo is persisted as a
 * feed key/slug and the actual copy into document storage happens server-side on
 * document save. Until that copy lands, the document-storage URL returned by
 * `getDefaultPhotoUrl` 404s. This returns the same feed proxy URL the product
 * search uses (which always resolves), so the photo can be shown immediately.
 *
 * Returns "" when no feed key is derivable or the photo is a blob/data/absolute
 * (non-feed) URL — the feed fallback would not help in those cases.
 */
// In-memory map: document_item_product_service_id -> a known-good display URL for
// an external-feed photo that has not yet been copied into document storage. The
// copy happens server-side on document save, so until a reload resolves the photo
// from the (public) document-storage blob, the row only has a generated
// destination slug that points nowhere. We capture the real feed display URL — the
// same one the product search/carousel renders in a plain <img> — at add time so
// the photo can be shown immediately. In-memory only; cleared on page reload.
// Shared footprint for the product photo shown inside the Short/Long Description
// column, so the layout editor, preview, external view and print all render it at
// the same size.
export const SHORT_LONG_DESCRIPTION_PHOTO_SIZE = { width: 80, height: 64 };

const pendingFeedDisplayUrlByItem = new Map();

export const rememberPendingFeedDisplayUrl = (documentItemProductServiceId, url) => {
  if (!documentItemProductServiceId || !url || typeof url !== "string") {
    return;
  }
  pendingFeedDisplayUrlByItem.set(documentItemProductServiceId, url);
};

export const getPendingFeedDisplayUrl = (documentItemProductServiceId) =>
  (documentItemProductServiceId && pendingFeedDisplayUrlByItem.get(documentItemProductServiceId)) || "";

// Test-only helper: the cache above is module-level and persists across mounts by
// design (cleared on page reload at runtime). Tests sharing a process must reset it
// in beforeEach/afterEach to stay isolated.
export const resetPendingFeedDisplayUrls = () => {
  pendingFeedDisplayUrlByItem.clear();
};

export const getFeedFallbackPhotoUrl = (productService) => {
  // A real feed URL captured when the item was added (before its document-storage
  // copy existed) is the most reliable fallback — prefer it over a slug-derived guess.
  const remembered = getPendingFeedDisplayUrl(productService?.document_item_product_service_id);
  if (remembered) {
    return remembered;
  }

  const normalized = normalizePhotos(productService);
  const rawEntries = getPhotoEntriesWithPendingMetadata(productService);
  const entries = rawEntries.length ? rawEntries : normalized;

  if (!entries.length) {
    return "";
  }

  const defaultIndex = normalized.length
    ? normalized.findIndex((item) => item.default_value)
    : getDefaultPhotoIndex(entries);
  const resolvedDefaultIndex = defaultIndex >= 0 ? defaultIndex : 0;
  const entry = entries[resolvedDefaultIndex] || entries[0];

  const rawUrl = getRawPhotoUrl(entry);
  if (!rawUrl || typeof rawUrl !== "string") {
    return "";
  }

  if (rawUrl.startsWith("blob:") || rawUrl.startsWith("data:")) {
    return "";
  }

  // An absolute, non-feed URL already resolves on its own.
  if (isAbsoluteUrl(rawUrl) && !isExternalFeedProxyUrl(rawUrl)) {
    return "";
  }

  const feedKey = entry?._feedKey || resolveFeedPhotoKey(rawUrl) || extractPhotoStorageKey(rawUrl);
  return feedKey ? getExternalFeedPhotoUrl(feedKey) : "";
};

export const buildPhotosForBundleTableDisplay = (productService, tenantId) => {
  const normalized = normalizePhotos(productService);
  const rawEntries = getPhotoEntriesWithPendingMetadata(productService);
  const sourceEntries = rawEntries.length ? rawEntries : normalized;

  if (!sourceEntries.length) {
    return [];
  }

  const defaultIndex = normalized.length
    ? normalized.findIndex((item) => item.default_value)
    : getDefaultPhotoIndex(sourceEntries);
  const resolvedDefaultIndex = defaultIndex >= 0 ? defaultIndex : 0;

  const photos = sourceEntries.map((entry, index) => {
    const normalizedMatch =
      normalized[index] ||
      normalized.find(
        (item) =>
          extractPhotoStorageKey(item.photo?.url) === extractPhotoStorageKey(entry?.photo?.url)
      );

    return {
      ...entry,
      default_value: Boolean(
        entry?.default_value ?? normalizedMatch?.default_value ?? index === resolvedDefaultIndex
      ),
      photo: {
        url:
          resolvePhotoEntryDisplayUrl(entry, productService, tenantId) ||
          entry?.photo?.url ||
          normalizedMatch?.photo?.url ||
          ""
      }
    };
  });

  return setDefaultPhoto(photos, resolvedDefaultIndex);
};

/** One from/to pair per stored photo when duplicating a product/service row. */
export const getPhotoDuplicatePairs = (oldPs, newPs, tenantId) => {
  if (!tenantId || !oldPs || !newPs) {
    return [];
  }

  return normalizePhotos(oldPs)
    .map((entry) => {
      const rawUrl = getRawPhotoUrl(entry);
      if (!rawUrl) {
        return null;
      }

      if (rawUrl.startsWith("blob:") || rawUrl.startsWith("data:")) {
        return null;
      }

      const slug = extractPhotoStorageKey(rawUrl);
      if (!slug) {
        return null;
      }

      const slugEntry = { photo: { url: slug } };
      const from = resolvePhotoDisplayUrl(slugEntry, oldPs, tenantId);
      const to = resolvePhotoDisplayUrl(slugEntry, newPs, tenantId);

      if (!from || !to || from === to) {
        return null;
      }

      return { from, to };
    })
    .filter(Boolean);
};

/**
 * For a product service configured in the document wizard (photos at settings/temp level),
 * generate from→to copy pairs for moving photos to document-level storage, and return
 * photo entries with bare filename slugs so they resolve correctly via document_item_product_service_id.
 *
 * Returns { pairs: Array<{from, to}>, normalizedPhotos: Array }
 */
export const getWizardAddItemPhotoCopyData = (productService, tenantId) => {
  const docPsId = productService?.document_item_product_service_id;
  if (!tenantId || !docPsId) {
    return { pairs: [], normalizedPhotos: normalizePhotos(productService) };
  }

  const pairs = [];
  const normalizedPhotos = normalizePhotos(productService).map((entry) => {
    const rawUrl = getRawPhotoUrl(entry);
    if (!rawUrl || rawUrl.startsWith("blob:") || rawUrl.startsWith("data:")) {
      return entry;
    }

    // Resolve where the photo currently lives (settings / temp), ignoring document_item_product_service_id
    const storagePath = extractProductServicePhotoStoragePath(rawUrl);
    const from = (() => {
      if (
        storagePath.startsWith("temp/productServicePhoto/") ||
        storagePath.startsWith("productServicePhoto/")
      ) {
        return `${AZURE_STORAGE_PREFIX}/${tenantId}/${storagePath}`;
      }
      if (productService.product_service_id) {
        const slug = extractPhotoStorageKey(rawUrl);
        return slug
          ? `${AZURE_STORAGE_PREFIX}/${tenantId}/productServicePhoto/${productService.product_service_id}/${slug}`
          : "";
      }
      return "";
    })();

    if (!from || !from.startsWith(AZURE_STORAGE_PREFIX)) return entry;

    // Bare filename for the destination slug
    const filename = (storagePath || extractPhotoStorageKey(rawUrl) || "").split("/").pop().split("?")[0];
    if (!filename) return entry;

    const to = `${AZURE_STORAGE_PREFIX}/${tenantId}/documents/${docPsId}/product_service/${filename}`;

    if (from !== to) {
      pairs.push({ from, to });
    }

    // Use bare filename so it resolves to document-level when displayed
    return { ...entry, photo: { url: filename } };
  });

  return { pairs, normalizedPhotos };
};

/**
 * Process all items in a wizard's itemsToAdd list:
 * - Generate media copy pairs (settings/temp → document level) for each PS and its children
 * - Normalize photo slugs to bare filenames so document-level resolution works
 *
 * Returns { normalizedItems: Array, mediaPairs: Array<{from, to}> }
 */
export const normalizeWizardItemsToAddForDocument = (itemsToAdd, tenantId) => {
  if (!itemsToAdd?.length || !tenantId) {
    return { normalizedItems: itemsToAdd || [], mediaPairs: [] };
  }

  const allPairs = [];

  const processPs = (ps) => {
    const { pairs, normalizedPhotos } = getWizardAddItemPhotoCopyData(ps, tenantId);
    allPairs.push(...pairs);

    const photosForApi = serializePhotosForApiSave({ ...ps, photos: normalizedPhotos });
    const defaultPhoto = getLegacyPhotoSlug({ photos: photosForApi });

    const children = Array.isArray(ps.children) ? ps.children.map(processPs) : ps.children;

    return {
      ...ps,
      photos: photosForApi,
      photo: defaultPhoto,
      children
    };
  };

  const normalizedItems = itemsToAdd.map(processPs);

  return { normalizedItems, mediaPairs: allPairs };
};

/** Strip display URLs from pasted rows — store slug-only photo metadata. */
export const normalizePastedProductServicePhotos = (productService) => {
  if (!productService) {
    return productService;
  }

  const serializedPhotos = serializePhotosForApiSave(productService);
  if (!serializedPhotos.length) {
    if (productService.url == null) {
      return productService;
    }

    const { url, ...rest } = productService;
    return rest;
  }

  return {
    ...productService,
    photos: serializedPhotos,
    photo: getLegacyPhotoSlug({ photos: serializedPhotos }),
    url: undefined
  };
};

export const setDefaultPhoto = (photos, activeIndex) => {
  if (!Array.isArray(photos) || !photos.length) {
    return [];
  }

  const normalizedIndex = Math.min(Math.max(activeIndex, 0), photos.length - 1);

  return photos.map((item, index) => ({
    ...item,
    default_value: index === normalizedIndex
  }));
};

const setRawPhotoPayload = (productService, photos) => {
  const next = { ...productService };
  const photosField = parseMaybeJson(next.photos ?? next.Photos ?? null);

  if (Array.isArray(photosField) && photosField.length) {
    if (next.photos != null) {
      next.photos = photos;
    }
    if (next.Photos != null) {
      next.Photos = photos;
    }
    return next;
  }

  const photoField = parseMaybeJson(next.photo ?? next.Photo ?? null);

  if (Array.isArray(photoField) && photoField.length) {
    if (next.photo != null) {
      next.photo = photos;
    }
    if (next.Photo != null) {
      next.Photo = photos;
    }
    return next;
  }

  if (next.photo != null) {
    next.photo = photos;
  } else if (next.Photo != null) {
    next.Photo = photos;
  } else {
    next.photo = photos;
  }

  return next;
};

export const applyDefaultPhotoIndex = (productService, activeIndex) => {
  const normalized = normalizePhotos(productService);
  if (!normalized.length) {
    return productService;
  }

  return setRawPhotoPayload(productService, setDefaultPhoto(normalized, activeIndex));
};

export const getDefaultPhotoIndex = (photos) => {
  if (!Array.isArray(photos) || !photos.length) {
    return 0;
  }

  const index = photos.findIndex((item) => item?.default_value === true);
  return index >= 0 ? index : 0;
};

export const isExternalFeedProxyUrl = (url) =>
  typeof url === "string" && url.includes("product_feed_image");

export const extractFeedPhotoKeyFromProxyUrl = (url) => {
  if (!isExternalFeedProxyUrl(url)) {
    return null;
  }

  try {
    const parsed = new URL(url, "http://localhost");
    const feedPath = parsed.searchParams.get("url");
    if (!feedPath) {
      return null;
    }

    if (feedPath.includes("/photo/")) {
      return feedPath.split("/photo/").pop() || null;
    }

    return feedPath;
  } catch (_error) {
    return null;
  }
};

/** Azure upload responses return a full URL; the API stores only the filename slug. */
export const extractPhotoStorageKey = (url) => {
  if (!url || typeof url !== "string") {
    return "";
  }

  const trimmed = url.trim();
  if (!trimmed) {
    return "";
  }

  const feedKey = extractFeedPhotoKeyFromProxyUrl(trimmed);
  if (feedKey) {
    return feedKey;
  }

  if (trimmed.includes("/photo/") && !isDocumentProductServiceStorageUrl(trimmed)) {
    return trimmed.split("/photo/").pop() || "";
  }

  const withoutQuery = trimmed.split("?")[0];
  const segments = withoutQuery.split("/").filter(Boolean);

  if (!segments.length) {
    return "";
  }

  if (segments.length === 1) {
    return segments[0];
  }

  return segments[segments.length - 1];
};

const PRODUCT_SERVICE_PHOTO_EXTENSIONS = new Set(["jpg", "jpeg", "png", "webp", "gif"]);

export const buildProductServicePhotoUploadFileName = (index = 0, originalFileName = "") => {
  const extensionMatch = String(originalFileName).match(/\.([a-zA-Z0-9]+)$/);
  const extension = extensionMatch?.[1]?.toLowerCase() || "jpg";
  const safeExtension = PRODUCT_SERVICE_PHOTO_EXTENSIONS.has(extension) ? extension : "jpg";
  return `product-service-photo-${index + 1}.${safeExtension}`;
};

export const buildProductServicePhotoUploadFile = (file, index = 0) => {
  if (!file) {
    return file;
  }

  const fileName = buildProductServicePhotoUploadFileName(index, file.name);
  if (file.name === fileName) {
    return file;
  }

  const extension = fileName.split(".").pop() || "jpg";
  const mimeType = file.type || (extension === "png" ? "image/png" : "image/jpeg");

  return new File([file], fileName, { type: mimeType });
};

export const resolvePhotoUrlForApiSave = (entry, productService, tenantId, options = {}) => {
  const rawUrl = getRawPhotoUrl(entry);
  if (!rawUrl || typeof rawUrl !== "string") {
    return "";
  }

  if (rawUrl.startsWith("blob:") || rawUrl.startsWith("data:")) {
    return "";
  }

  if (isAbsoluteUrl(rawUrl) && !isExternalFeedProxyUrl(rawUrl)) {
    if (shouldNormalizeToCanonicalAzureUrl(rawUrl) || rawUrl.includes(AZURE_STORAGE_PREFIX)) {
      return rawUrl.split("?")[0];
    }
    return rawUrl.split("?")[0];
  }

  return (
    resolvePhotoEntryUrl(entry, productService, tenantId, options) ||
    resolvePhotoDisplayUrl(entry, productService, tenantId, options) ||
    ""
  );
};

export const serializePhotosForApiSave = (productService, options = {}) => {
  const { useFullUrls = false, tenantId, productServiceContext } = options;
  const context = productServiceContext || productService;

  return normalizePhotos(productService).map(({ photo, default_value }) => {
    const url = useFullUrls
      ? resolvePhotoUrlForApiSave({ photo }, context, tenantId, options)
      : extractProductServicePhotoStoragePath(photo?.url);

    return {
      photo: { url },
      default_value: Boolean(default_value)
    };
  });
};

export const getLegacyPhotoSlug = (productService) => {
  const photos = serializePhotosForApiSave(productService);
  if (photos.length) {
    const defaultEntry = photos.find((item) => item.default_value) || photos[0];
    return defaultEntry?.photo?.url || null;
  }

  const legacy = productService?.photo ?? productService?.url ?? null;
  if (!legacy) {
    return null;
  }

  if (typeof legacy === "string") {
    return extractPhotoStorageKey(legacy);
  }

  return extractPhotoStorageKey(legacy?.url);
};

/** Serialize photo fields for document upsert — nested children may only persist legacy `photo`. */
export const prepareProductServicePhotoFieldsForApiSave = (productService, options = {}) => {
  const normalized = normalizePastedProductServicePhotos(productService);
  const photos = serializePhotosForApiSave(normalized, options);

  if (!photos.length) {
    return { photos: [], photo: null };
  }

  if (photos.length === 1) {
    return { photos, photo: photos[0].photo.url };
  }

  return {
    photos,
    photo: JSON.stringify(
      photos.map(({ photo, default_value }) => ({
        photo: photo.url,
        default_value
      }))
    )
  };
};

/** Bundle children should send full azure URLs to the settings API. */
export const prepareBundleChildPhotoFieldsForApiSave = (child, tenantId) =>
  prepareProductServicePhotoFieldsForApiSave(child, {
    useFullUrls: true,
    tenantId,
    productServiceContext: child
  });

/** Apply photo field serialization to a product/service tree for document upsert payloads. */
export const prepareProductServiceTreeForApiUpsert = (productServices) =>
  (productServices || [])
    .filter((ps) => !ps?.is_inside_bundle)
    .map((ps) => {
      const parentPhotoFields = prepareProductServicePhotoFieldsForApiSave(ps);
      const children = (ps.children || []).map((child) => {
        const childPhotoFields = prepareProductServicePhotoFieldsForApiSave(child);
        return {
          ...child,
          photos: childPhotoFields.photos,
          photo: childPhotoFields.photo
        };
      });

      return {
        ...ps,
        photos: parentPhotoFields.photos,
        photo: parentPhotoFields.photo,
        children
      };
    });

export const photosForProductServiceDisplay = (storedPhotos, productService, tenantId, options = {}) => {
  const withPhotos = { ...productService, photos: storedPhotos };
  const displayOptions = {
    preferExternalFeed: options.preferExternalFeed ?? isExternalFeedProduct(productService),
    ...options
  };

  return normalizePhotos(withPhotos).map((entry) => ({
    ...entry,
    photo: {
      url: resolvePhotoDisplayUrl(entry, productService, tenantId, displayOptions)
    }
  }));
};

/** Read photo entries including pending upload metadata (_pendingFile, _base64). */
export const getPhotoEntriesWithPendingMetadata = (productService) => {
  const photosField = productService?.photos ?? productService?.Photos;
  if (Array.isArray(photosField) && photosField.length) {
    return photosField;
  }

  return normalizePhotos(productService);
};

export const photoEntryNeedsSettingsUpload = (entry) => photoEntryHasPendingUpload(entry);

export const productServiceHasPendingPhotoUpload = (productService) =>
  getPhotoEntriesWithPendingMetadata(productService).some(photoEntryNeedsSettingsUpload);

export const buildProductServicePhotoUploadPath = (productServiceId, { useTemp = false } = {}) =>
  useTemp
    ? `/tenantUser/product_service/photo/temp/${productServiceId}`
    : `/tenantUser/product_service/photo/${productServiceId}`;

/** Stage bundle-child photos under /photo/temp until the parent bundle is saved. */
export const shouldUseTempProductServicePhotoUpload = ({
  isFromDocument,
  isNew,
  persistedProductServiceId,
  pickedProductServiceId
} = {}) => {
  if (isFromDocument) {
    return false;
  }
  if (!isNew && persistedProductServiceId) {
    return false;
  }
  if (pickedProductServiceId) {
    return false;
  }
  return true;
};

/** Keep pending upload metadata in memory until the parent product/service is saved. */
export const preparePhotosForDeferredStorage = (photoEntries) =>
  (photoEntries || []).map((entry) => ({
    photo: { url: entry?.photo?.url || "" },
    default_value: Boolean(entry?.default_value),
    ...(entry?._pendingFile && { _pendingFile: entry._pendingFile }),
    ...(entry?._base64 && { _base64: entry._base64 }),
    ...(entry?._feedKey && { _feedKey: entry._feedKey })
  }));

/** Strip in-memory photo blobs/files before sending a staged bundle child to the API. */
export const stripPendingPhotosForApiSave = (productService) => {
  if (!productServiceHasPendingPhotoUpload(productService)) {
    const photoFields = prepareProductServicePhotoFieldsForApiSave(productService);
    return {
      ...productService,
      photos: photoFields.photos,
      photo: photoFields.photo
    };
  }

  const { photos: _photos, photo: _photo, url: _url, ...rest } = productService;
  return {
    ...rest,
    photos: [],
    photo: null
  };
};

/** Normalize document PS photo upload API responses (single or batch). */
export const parseDocumentPhotoUploadUrls = (data) => {
  if (Array.isArray(data?.imgUrls)) {
    return data.imgUrls;
  }

  if (Array.isArray(data?.imgUrl)) {
    return data.imgUrl;
  }

  if (data?.imgUrl) {
    return [data.imgUrl];
  }

  return [];
};

/** Normalize settings PS photo upload API responses (single or batch). */
export const parseSettingsPhotoUploadUrls = (data) => {
  const batchUrls = parseDocumentPhotoUploadUrls(data);
  if (batchUrls.length) {
    return batchUrls;
  }

  if (Array.isArray(data?.urls)) {
    return data.urls;
  }

  if (data?.url) {
    return [data.url];
  }

  return [];
};

export const buildProductServicePhotoUploadFormData = (files = []) => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append("photo", file, file.name || "product-service-photo.jpg");
  });
  return formData;
};

export const mapSettingsPhotoUploadResults = (photoEntries, filesToUpload, uploadedUrls) => {
  const uploadedPhotos = new Array(photoEntries.length).fill(null);

  photoEntries.forEach((photoEntry, index) => {
    const resolvedUrl = photoEntry?.photo?.url || "";
    if (resolvedUrl && !photoEntryNeedsSettingsUpload(photoEntry)) {
      uploadedPhotos[index] = {
        photo: { url: extractProductServicePhotoStoragePath(resolvedUrl) },
        default_value: Boolean(photoEntry?.default_value)
      };
    }
  });

  filesToUpload.forEach((item, uploadIndex) => {
    const resolvedUrl = uploadedUrls[uploadIndex];
    if (!resolvedUrl) {
      return;
    }

    const photoEntry = photoEntries[item.entryIndex];
    uploadedPhotos[item.entryIndex] = {
      photo: { url: extractProductServicePhotoStoragePath(resolvedUrl) },
      default_value: Boolean(photoEntry?.default_value)
    };
  });

  const result = uploadedPhotos.filter(Boolean);

  if (result.length && !result.some((item) => item.default_value)) {
    result[0].default_value = true;
  }

  return result;
};
